import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import { Loader2, Clock, DollarSign, Trash2 } from 'lucide-react';
import { fmtDate, fmtWindow, money, addDays } from '../../../lib/scheduleFormat';

// Hours the bakery actually rents. The cost shown is informational — kitchen
// rent is never invoiced or paid through the portal.
const BookingDialog = ({
  open, kitchen, kitchens, booking, date, availability, onClose, onSubmit, onDelete, busy,
}) => {
  const [kitchenId, setKitchenId] = useState('');
  const [dateStr, setDateStr] = useState('');
  const [startTime, setStartTime] = useState('05:00');
  const [endTime, setEndTime] = useState('11:00');
  const [status, setStatus] = useState('planned');
  const [notes, setNotes] = useState('');
  const [serves, setServes] = useState([]);

  useEffect(() => {
    if (!open) return;
    setKitchenId(booking?.kitchen_id || kitchen?.id || kitchens?.[0]?.id || '');
    setDateStr(booking?.date || date || '');
    setStartTime(booking?.start_time || '05:00');
    setEndTime(booking?.end_time || '11:00');
    setStatus(booking?.status || 'planned');
    setNotes(booking?.notes || '');
    // Production is the night before delivery here, so a new booking defaults
    // to serving the next day rather than its own.
    setServes(booking?.serves_delivery_dates || (date ? [addDays(date, 1)] : []));
  }, [open, booking, date, kitchen, kitchens]);

  const activeKitchen = useMemo(
    () => (kitchens || []).find((k) => k.id === kitchenId) || kitchen,
    [kitchens, kitchenId, kitchen]
  );

  const hours = useMemo(() => {
    if (!startTime || !endTime) return 0;
    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    let minutes = eh * 60 + em - (sh * 60 + sm);
    if (minutes <= 0) minutes += 1440;
    return minutes / 60;
  }, [startTime, endTime]);

  const rate = booking ? booking.hourly_rate : (activeKitchen?.hourly_rate || 0);
  const servingOptions = dateStr
    ? [addDays(dateStr, 0), addDays(dateStr, 1), addDays(dateStr, 2), addDays(dateStr, 3)]
    : [];
  const options = Array.from(new Set([...servingOptions, ...serves])).sort();

  const toggleServe = (value) =>
    setServes((prev) => (prev.includes(value) ? prev.filter((d) => d !== value) : [...prev, value]));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle>{booking ? 'Edit kitchen booking' : 'Book kitchen hours'}</DialogTitle>
          <DialogDescription>
            These are the hours you rent and pay for. They must sit inside the landlord&apos;s access
            agreement.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {(kitchens || []).length > 1 && (
            <div>
              <Label htmlFor="booking-kitchen">Kitchen</Label>
              <select
                id="booking-kitchen"
                className="mt-1 h-10 w-full rounded-md border border-gray-300 bg-white px-2 text-sm"
                value={kitchenId}
                onChange={(e) => setKitchenId(e.target.value)}
                data-testid="booking-kitchen"
              >
                {(kitchens || []).map((k) => (
                  <option key={k.id} value={k.id}>{k.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label htmlFor="booking-date">Date</Label>
              <Input id="booking-date" type="date" value={dateStr}
                onChange={(e) => setDateStr(e.target.value)} data-testid="booking-date" />
            </div>
            <div>
              <Label htmlFor="booking-start">From</Label>
              <Input id="booking-start" type="time" value={startTime}
                onChange={(e) => setStartTime(e.target.value)} data-testid="booking-start" />
            </div>
            <div>
              <Label htmlFor="booking-end">To</Label>
              <Input id="booking-end" type="time" value={endTime}
                onChange={(e) => setEndTime(e.target.value)} data-testid="booking-end" />
            </div>
          </div>

          {availability?.length > 0 && (
            <div className="rounded-lg bg-slate-50 p-3 text-xs text-gray-700" data-testid="booking-availability-hint">
              <p className="font-medium text-gray-900">Kitchen access on {fmtDate(dateStr)}</p>
              <p className="mt-1">
                {availability.map((w) => fmtWindow(w.start_time, w.end_time)).join('  ·  ')}
              </p>
              {activeKitchen && (
                <p className="mt-1 text-gray-500">
                  Minimum {activeKitchen.min_block_minutes} min, in {activeKitchen.booking_increment_minutes}-min
                  increments.
                </p>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <span className="flex items-center gap-1 text-sm font-medium text-gray-900">
              <Clock className="h-4 w-4 text-amber-700" /> {hours.toFixed(hours % 1 === 0 ? 0 : 1)} hours
            </span>
            <span className="flex items-center gap-1 text-sm font-medium text-gray-900" data-testid="booking-cost">
              <DollarSign className="h-4 w-4 text-amber-700" /> {money(hours * rate)}
            </span>
            <span className="text-xs text-gray-600">at {money(rate)}/hour · informational only</span>
          </div>

          <div>
            <Label>Delivery dates this run serves</Label>
            <p className="mt-1 mb-2 text-xs text-gray-500">
              The coverage board reads this to decide whether a delivery date has kitchen hours behind it.
            </p>
            <div className="flex flex-wrap gap-2" data-testid="booking-serves">
              {options.map((option) => (
                <label
                  key={option}
                  className="flex cursor-pointer items-center gap-2 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
                  data-testid={`booking-serves-${option}`}
                >
                  <Checkbox checked={serves.includes(option)} onCheckedChange={() => toggleServe(option)} />
                  {fmtDate(option)}
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label>Landlord status</Label>
            <div className="mt-2 flex gap-2">
              {['planned', 'confirmed'].map((value) => (
                <Button
                  key={value}
                  type="button"
                  variant={status === value ? 'default' : 'outline'}
                  className={status === value ? 'bg-amber-600 hover:bg-amber-700' : ''}
                  onClick={() => setStatus(value)}
                  data-testid={`booking-status-${value}`}
                >
                  {value === 'planned' ? 'Planned' : 'Confirmed with landlord'}
                </Button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="booking-notes">Notes (optional)</Label>
            <Textarea id="booking-notes" value={notes} rows={2}
              onChange={(e) => setNotes(e.target.value)} data-testid="booking-notes" />
          </div>

          {booking?.conflict && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
              data-testid="booking-conflict">
              <Badge variant="outline" className="mr-2 bg-white text-red-700">Conflict</Badge>
              {booking.conflict.message}
            </div>
          )}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
          {booking ? (
            <Button
              variant="outline" className="text-red-600 hover:bg-red-50"
              onClick={() => onDelete(booking)} disabled={busy}
              data-testid="booking-delete"
            >
              <Trash2 className="h-4 w-4 mr-1" /> Cancel booking
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="booking-cancel">Close</Button>
            <Button
              onClick={() => onSubmit({
                kitchen_id: kitchenId,
                date: dateStr,
                start_time: startTime,
                end_time: endTime,
                status,
                notes,
                serves_delivery_dates: serves,
              })}
              disabled={busy || !dateStr || !kitchenId}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="booking-save"
            >
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {booking ? 'Save booking' : 'Book hours'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BookingDialog;
